import React from 'react';
import { Alert, Box, Button, CircularProgress, Divider, List, ListItem, Modal, Paper, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';
import ViaRufnummer from './components/ViaRufnummer';
import ViaAdresse from './components/ViaAdresse';
import { NotrufEntry } from './utils/Backend';

export enum TabEnum {
  ViaRufnummer = 0,
  ViaAdresse = 1
}

export function App() {

  const [tab, setTab] = React.useState(TabEnum.ViaRufnummer);

  const handleChange = (event: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 10 }}>
        <img src={require("./assets/Logo.jpg")} style={{ height: "120px", minHeight: "120px", maxWidth: "min(100vw, 400px)", width: "auto" }} alt="ImmoConcept Hillemeier Logo" />
      </div>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Paper style={{ padding: 10, margin: 5, overflowY: "auto", height: "calc(100dvh - 210px)", maxWidth: "580px" }} >

          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", marginBottom: 0 }}>
            <Typography variant='h5' textAlign={"center"}>Ihr Störungs- und Handwerkerverzeichnis</Typography>
          </div>
          <Tabs value={tab} onChange={handleChange} style={{ marginBottom: 15 }}>
            <Tab label="Via Telefonnummer" id={TabEnum.ViaRufnummer.toString()} />
            <Tab label="Via Adresse" id={TabEnum.ViaAdresse.toString()} />
          </Tabs>
          {tab === TabEnum.ViaRufnummer && <ViaRufnummer changeTab={setTab} />}
          {tab === TabEnum.ViaAdresse && <ViaAdresse changeTab={setTab} />}
        </Paper>
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, width: "100%", height: "50px", background: "lightblue" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          © ImmoConcept Hillemeier GmbH & Co. KG 2026
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <a href="https://icvg.de/impressum/" target='_blank' rel="noreferrer" style={{ marginRight: 10 }}>Impressum</a>
          <a href="https://icvg.de/datenschutz/" target='_blank' rel="noreferrer">Datenschutz</a>
        </div>
      </div>
    </>
  );
}

export function NotrufList(props: { entries: NotrufEntry[] }) {

  const [dialog, setDialog] = React.useState(false);
  const [dialogItem, setDialogItem] = React.useState<NotrufEntry | null>(null);
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState<"default" | "error" | "success" | "loading">("default");

  const getName = (item: NotrufEntry) => {
    if ("name" in item) return item.name;
    return item.objektString;
  };

  const getLink = (item: NotrufEntry) => {
    if ("link" in item) return item.link;
    return null;
  };

  const getMailLink = (item: NotrufEntry) => {
    if ("mailLink" in item) return item.mailLink;
    return null;
  };

  const getEinheitenStrassen = (item: NotrufEntry) => {
    if ("einheitenStrassen" in item) return item.einheitenStrassen;
    return null;
  }

  const openDialog = (item: NotrufEntry) => {
    setDialogItem(item);
    setDialog(true);
    setStatus("default");
    setEmail("");
  };

  const sendMail = (email: string) => {
    email = email.trim().toLowerCase();
    const mailLink = dialogItem ? getMailLink(dialogItem) : null;

    if (testMail(email) && mailLink) {
      setStatus("loading");
      fetch(mailLink + "&mail=" + encodeURIComponent(email))
        .then(() => {
          setStatus("success");
          setTimeout(() => setDialog(false), 1500);
        })
        .catch(() => setStatus("error"));
    }
    else {
      alert("Bitte geben Sie eine gültige E-Mail Adresse ein.");
    }
  };

  const center = {
    position: 'absolute',
    top: '50%',
    left: '47%',
    transform: 'translate(-50%, -50%)',
  };

  const style = {
    ...center,
    width: 350,
    bgcolor: 'background.paper',
    border: '1px solid #000',
    boxShadow: 24,
    p: 4,
  };

  return <>
    <Modal open={dialog} onClose={() => setDialog(false)}>
      <Box sx={style}>
        {status === "default" &&
          <>
            <Typography id="modal-modal-title" variant="h6" component="h2" style={{ marginBottom: 4 }}>
              An welche E-Mail Adresse möchten Sie Ihr Störungs- und Handwerkerverzeichnis für {dialogItem && getName(dialogItem)} erhalten?
            </Typography>
            <TextField variant="outlined" type='email' value={email} fullWidth label="E-Mail" onChange={(e: any) => {
              setEmail(e.target.value);
            }}
              onKeyDown={(e: any) => {
                if (e.key === "Enter") {
                  sendMail(e.target.value);
                }
              }}
              InputProps={{ endAdornment: <Button variant='text' onClick={() => sendMail(email)}>Senden</Button> }} />
          </>}
        {status === "error" &&
          <Alert severity='error'>Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.</Alert>}
        {status === "success" &&
          <Alert severity='success'>Ihre E-Mail wurde erfolgreich versendet.</Alert>}
        {status === "loading" && <div style={{ minHeight: 100 }}>
          <CircularProgress sx={center} />
        </div>}
      </Box>
    </Modal>

    <List>
      {props.entries.map((item, index) => (
        <React.Fragment key={index}>
          {index !== 0 && <Divider />}
          <ListItem key={index}>
            <div style={{ display: "flex", flexDirection: "column" }}>

              <Typography variant="h6">{getName(item)}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginLeft: 10 }}>
                {getEinheitenStrassen(item)}
              </Typography>
            </div>

            <Tooltip title="Per E-Mail erhalten">
              <Button variant='contained' sx={{ marginLeft: 'auto' }}
                onClick={() => openDialog(item)}>
                <AttachEmailIcon />
              </Button>
            </Tooltip>

            {getLink(item) && (
              <Tooltip title="Herunterladen">
                <Button variant='contained' sx={{ marginLeft: '2px' }}
                  href={getLink(item) ?? ""} target='_blank' rel="noreferrer">
                  <DownloadIcon />
                </Button>
              </Tooltip>
            )}
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  </>;
}

function testMail(s: string): boolean {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(s);
}